import {HttpClient, HttpResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {Institution} from "../../models/institution";
import {KeycloakGroup} from "../../models/keycloak-group";
import {InstitutionUnit} from "../../models/Institution-unit";
import Helpers from "../../utils/helpers";
import {RegistrationInstitution} from "../../models/registration-institution";
import {RoleInstitution} from "../../models/role-institution";
import { map } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Injectable()
export class InstitutionControllerService {

  private baseUrl: string = environment.PARTC_URL;
  private keycloakInstitutionsGroupId: string = environment.KEYCLOAK_INSTITUTIONS_GROUP_ID;
  private keycloakBaseUrl: string = environment.KEYCLOAK_URL + "admin/realms/" + environment.KEYCLOAK_REALM;
  constructor(private http: HttpClient) {}

  private mapKeycloakGroupsIntoInstitution(institution: RegistrationInstitution, groups: KeycloakGroup[]): Institution {
    const inst = new Institution();
    inst.id = institution.institutionId;
    inst.name = institution.name;
    inst.units = groups.map((sg) => {
      const unit = new InstitutionUnit();
      unit.id = sg.id;
      unit.name = sg.name;
      unit.institution_id = institution.institutionId;
      return unit;
    });
    return inst;
  }

  private mapKeycloakGroupsIntoInstitutionByRoleInstitution(institution: RoleInstitution, groups: KeycloakGroup[]): Institution {
    const inst = new Institution();
    inst.id = institution.institutionId;
    inst.units = groups.map((sg) => {
      const unit = new InstitutionUnit();
      unit.id = sg.id;
      unit.name = sg.name;
      unit.url = sg.attributes?.url?.[0];
      unit.institution_id = institution.institutionId;
      return unit;
    });
    return inst;
  }

  private mapKeycloakGroupIntoInstitution(group: KeycloakGroup): Institution {
    const institution = new Institution();
    institution.id = group.id;
    institution.name = group.name;
    institution.shortName = group.attributes?.shortName?.length > 0 && group.attributes.shortName[0];
    institution.url = group.attributes?.url?.length > 0 && group.attributes.url[0];
    institution.units = group.subGroups.map((sg) => {
      const unit = new InstitutionUnit();
      unit.id = sg.id;
      unit.name = sg.name;
      unit.institution_id = group.id;
      return unit;
    });
    return institution;
  }
  private mapInstitutionIntoKeycloakGroup(institution: Institution): KeycloakGroup {
    // this method ignores subgroups
    const group = new KeycloakGroup();
    group.id = institution.id;
    group.name = institution.name;
    if (institution.shortName) { group.attributes.shortName = [institution.shortName]; }
    if (institution.url) { group.attributes.url = [institution.url]; }
    return group;
  }

  private getInstitutionsKeycloak(): Observable<Institution[]> {
    console.log("requesting institutions in a new way");
    if (this.keycloakInstitutionsGroupId) {
      return (this.http.get(this.keycloakBaseUrl + "/groups/" + this.keycloakInstitutionsGroupId + "/children?max=200") as Observable<KeycloakGroup[]>)
          .pipe(map((groups) => {
            return groups.map((sg) => {
              return this.mapKeycloakGroupIntoInstitution(sg);
            });
          }));
    }
    return (this.http.get(this.keycloakBaseUrl + "/groups") as Observable<KeycloakGroup[]>)
        .pipe(map((groups) => {
          return groups.map((group) => {
            return this.mapKeycloakGroupIntoInstitution(group);
          });
        }));
  }

  public getInstitutions(): Observable<Institution[]> {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return this.getInstitutionsKeycloak();
    }
    return this.http.get(this.baseUrl + "institution/all") as Observable<Institution[]> ;
  }

  public getAllInstitutionsAndUnits(): Observable<Institution[]> {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return this.getInstitutionsKeycloak();
    }
    return this.http.get(this.baseUrl + "institution/allFull") as Observable<Institution[]> ;
  }

  public createInstitution(institution: Institution): Observable<HttpResponse<any>> | Observable<string> {
    Helpers.trimRecursiveObj(institution);
    const body = JSON.stringify(institution);
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      // keycloak returns 201 with an empty body in case of all good.
      // id of the new object must be read from the response headers.
      if (this.keycloakInstitutionsGroupId) {
        return this.createInstitutionUnit(this.keycloakInstitutionsGroupId, institution);
      }
      return this.http.post(this.keycloakBaseUrl + "/groups", this.mapInstitutionIntoKeycloakGroup(institution),
          { observe: "response" }) as Observable<HttpResponse<any>>;
    }
    return this.http.put(this.baseUrl + "institution", body, {responseType: "text"});
  }

  // only keycloak method
  public createInstitutionUnit(parentId: string, institution: Institution): Observable<HttpResponse<any>> {
    Helpers.trimRecursiveObj(institution);
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return this.http.post(this.keycloakBaseUrl + "/groups/" + parentId + "/children",
          this.mapInstitutionIntoKeycloakGroup(institution), { observe: "response" }) as Observable<HttpResponse<any>>;
    }
    return EMPTY;
  }

  public getInstitution(institutionId: string): Observable<Institution> {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return (this.http.get(this.keycloakBaseUrl + "/groups/" + institutionId) as Observable<KeycloakGroup>)
        .pipe(map((group) => {
          return this.mapKeycloakGroupIntoInstitution(group);
        }));
    }
    return this.http.get(this.baseUrl + "institution/" + institutionId) as Observable<Institution>;
  }

  public getInstitutionChildren(registrationInstitution: RegistrationInstitution): Observable<Institution> {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return (this.http.get(this.keycloakBaseUrl + "/groups/" + registrationInstitution.institutionId + "/children?max=200") as Observable<KeycloakGroup[]>)
        .pipe(map((groups) => {
          return this.mapKeycloakGroupsIntoInstitution(registrationInstitution, groups);
      }));
    }
    return this.http.get(this.baseUrl + "institution/" + registrationInstitution.institutionId) as Observable<Institution>;
  }

  public getInstitutionChildrenByRoleInstitution(roleInstitution: RoleInstitution): Observable<Institution> {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return (this.http.get(this.keycloakBaseUrl + "/groups/" + roleInstitution.institutionId + "/children?max=200") as Observable<KeycloakGroup[]>)
        .pipe(map((groups) => {
          return this.mapKeycloakGroupsIntoInstitutionByRoleInstitution(roleInstitution, groups);
        }));
    }
    return this.http.get(this.baseUrl + "institution/" + roleInstitution.institutionId) as Observable<Institution>;
  }

  public updateInstitution(institution: Institution) {
    Helpers.trimRecursiveObj(institution);
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return this.http.put(this.keycloakBaseUrl + "/groups/" + institution.id,
          this.mapInstitutionIntoKeycloakGroup(institution));
    }
    const body = JSON.stringify(institution);
    return this.http.post(this.baseUrl + "institution/" + institution.id, body);
  }

  public deleteInstitution(institution: Institution) {
    if (Boolean(environment.KEYCLOAK_ACTIVE)) {
      return this.http.delete(this.keycloakBaseUrl + "/groups/" + institution.id);
    }
    return this.http.delete(this.baseUrl + "institution/" + institution.id);
  }

}
